import { LOG_SERVICE, LogService } from "@cervval.js/common/services";
import { injector } from "@cervval.js/common/src/dependencies/injector";
import { getProp, setProp } from "@cervval.js/common/src/tools/any.tools";
import { isNumeric } from "@cervval.js/common/src/tools/number.tools";
import { anyReallyAny } from "@cervval.js/common/src/tools/object.tools";
import { ObjectTools } from "@cervval.js/common/tools";
import { TypeInfos } from "@cervval.js/ts-reflect";
import { WebComponent } from "./webComponent";

type DefaultValues = Record<string, anyReallyAny>;

export const TYPED_WEB_COMPONENT_OPTIONS_PROXY_CACHE_SUFFIX = "_proxy_cache";

/**
 * WebComponentOptions
 * Provides direct readonly access to options and set methods that can call WebComponent.refresh after options change
 */
export type WebComponentOptions<Options> = Readonly<Options> & {
    /**
     * Overrides options and calls refresh if at least one option was changed
     * @param options options to override
     * @returns true if at least one option was changed
     */
    setAndRefreshIfChanged(options?: Partial<Options>): boolean,

    /**
     * Overrides options and does NOT call refresh
     * @param options options to override
     * @returns true if at least one option was changed
     */
    setWithoutRefresh(options?: Partial<Options>): boolean
}

/**
 * Overrides options and calls refresh if at least one option was changed
 * @returns true if at least one option was changed
 */
function setOptions(currentOptions: Record<string, unknown>, optionsToOverride: Record<string, unknown>, webComponent: WebComponent, callRefresh: boolean, refreshFunction?: string): boolean {
    if (optionsToOverride != null) {
        let changed = false;
        // Override currentOptions with the keys provided in optionsToOverride
        for (const key in optionsToOverride) {
            const value = optionsToOverride[key];

            if (value !== currentOptions[key]) {
                currentOptions[key] = value;
                changed = true;
            }
        }
        if (changed && callRefresh) {
            // refresh WebComponent
            if (refreshFunction) {
                const refreshFunc = getProp(webComponent, refreshFunction);
                if (typeof refreshFunc === "function") {
                    refreshFunc();
                } else {
                    injector.get<LogService>(LOG_SERVICE).error("component does not have a " + refreshFunction + " function");
                }
            } else if (webComponent.isInitDone) {
                // if !isInitDone, refresh will be called later
                webComponent.refresh();
            }
        }
        return changed;
    }
    return false;
}

/**
 * TypedWebComponentOptions is a property decorator that handles WebComponent options
 * TODO details method
 * attributes binding
 * parse with type
 * @param params
 * @param typeInfos 
 * @returns 
 */
export function TypedWebComponentOptions<T>(params?: DefaultValues | { defaultValues?: DefaultValues, doNotBindOptionsToHtmlAttributes?: boolean, attributePrefix?: string, refreshFunction?: keyof T }, typeInfos?: TypeInfos): (classPrototype: T, optionsKey: string) => void {
    return (classPrototype: T, optionsKey: string): void => {

        const doNotBindHtmlAttributes = params?.doNotBindOptionsToHtmlAttributes ?? false;
        const attributePrefix = params?.attributePrefix ?? "";
        const defaultValues: DefaultValues = (params?.attributePrefix ? params?.defaultValues as DefaultValues : params as DefaultValues) ?? {};
        if (!typeInfos?.properties) {
            console.error("TypedWebComponentOptions could not identify properties with provided Type ", typeInfos);
        }
        const proxyKey = "__" + optionsKey + TYPED_WEB_COMPONENT_OPTIONS_PROXY_CACHE_SUFFIX;
        const getProxy = (element: WebComponent): unknown => {
            const proxy = getProp(element, proxyKey);
            if (proxy) { return proxy; }// proxy was already built => return it

            // add set functions
            const setAndRefreshIfChanged = (optionsToOverride: Record<string, unknown>): boolean => {
                return setOptions(getProp(element, proxyKey), optionsToOverride, element, true, params?.refreshFunction);
            };
            const setWithoutRefresh = (optionsToOverride: Record<string, unknown>): boolean => {
                return setOptions(getProp(element, proxyKey), optionsToOverride, element, false, undefined);
            };

            // Build proxy
            const handler: ProxyHandler<anyReallyAny> = {
                get: (options: anyReallyAny, propertyKey: string): unknown | undefined => {

                    if (propertyKey === "setAndRefreshIfChanged") { return setAndRefreshIfChanged; }
                    if (propertyKey === "setWithoutRefresh") { return setWithoutRefresh; }
                    if (propertyKey === "optionPropertyKeys") { return typeInfos; }

                    if (!doNotBindHtmlAttributes && element.hasAttribute(attributePrefix + propertyKey)) {
                        // if property is defined in an attribute, it overrides the option value
                        const value = element.getAttribute(attributePrefix + propertyKey);
                        if (typeInfos?.properties) {
                            // Handle value with typeInfos (convert from string to correct type)
                            const handled = handleTypes(value, typeInfos, propertyKey);
                            if (handled.isHandled) {
                                return handled.value;
                            }
                        }
                        return value;
                    } else {
                        return options[propertyKey];
                    }
                },
                set: (options: anyReallyAny, propertyKey: string, value: unknown): boolean => {
                    options[propertyKey] = value;

                    if (!doNotBindHtmlAttributes && element.hasAttribute(attributePrefix + propertyKey)) {
                        // property is set as an attribute on the element
                        if ((typeof value === "number" || typeof value === "string") && propertyKey !== "value") {
                            // if it is a number or string value, we update the attribute in order to see its change in the developer console
                            // if it is the "value" property we don't set it as an attribute since it may be redundant
                            element.setAttribute(attributePrefix + propertyKey, String(value));
                        } else {
                            // otherwise, we simply remove the attribute
                            element.removeAttribute(attributePrefix + propertyKey);
                        }
                    }
                    return true;
                }
            };

            // Build WebComponentOptions object (options proxy + set functions)
            const cloneOptions = ObjectTools.clone(defaultValues, { keepArrayReferences: false });// clone defaultValues and ensure we don't keep array references
            const optionsProxy = new Proxy<anyReallyAny>(cloneOptions, handler);
            // store proxy in instance
            setProp(element, proxyKey, optionsProxy);

            // return proxy
            return optionsProxy;
        };

        Object.defineProperty(classPrototype, optionsKey, {
            get: function () { return getProxy(this); }
        });


    };
}

/**
 * Object returned by **handler** methods
 */
interface HandlerReturn {
    isHandled: boolean;
    value: unknown;
}

/**
 * Not handled return
 */
function notHandled(value: unknown): HandlerReturn {
    return {
        isHandled: false,
        value
    };
}

// Check type equality, add object catch
const checkTypeString = (type: string | undefined, typeToCheck: string): boolean => (type?.includes(typeToCheck) && !type?.includes("{") && !type?.includes("}")) ?? false;

/**
 * Check if type exist in "type" field of TypeInfos.
 * Value of TypeInfos matched are "string" / "number" / "Array"(string & number) / "Boolean" / "Date" / "undefined"
 * Type: "number | Date | undefined" will match first for "number", then "Date", and finally "undefined"
 * @param typeInfo Hold type value to handle
 * @param typeStringToCheck Type of value to handle
 */
function checkTypeExist(typeInfo: TypeInfos, typeStringToCheck: string | string[], propertyKey: string): boolean {


    if (typeof typeStringToCheck === "string") { // String

        return checkTypeString(typeInfo.properties?.[propertyKey]?.type, typeStringToCheck);
    } else { // String[]
        for (const stringToCheck of typeStringToCheck) {

            if (checkTypeString(typeInfo.properties?.[propertyKey]?.type, stringToCheck)) {
                return true;
            }
        }
        return false;
    }
}

/**
 * Handle types, update value in input if a match is found by any handler
 * Handle loop is stopped if a match is found.
 * @param value to type handle check
 * @param typeInfos Typed decorator => typeInfos parameter added at build time (see utils/reflect-ts)
 */
function handleTypes(value: unknown, typeInfos: TypeInfos, propertyKey: string): HandlerReturn {

    // Define array functions of handlers, to avoid useless calls when a handler match
    // ! Respect array always first handler order
    const handlers = [
        (): HandlerReturn => arrayHandler(value, typeInfos, propertyKey),
        (): HandlerReturn => numberHandler(value, typeInfos, propertyKey),
        (): HandlerReturn => booleanHandler(value, typeInfos, propertyKey),
        (): HandlerReturn => objectHandler(value, typeInfos, propertyKey)
    ];// TODO handle Timestamps ?

    // Loop for all handlers
    for (const handler of handlers) {
        const handlerReturn = handler();

        // Check a handler match
        if (handlerReturn.isHandled) {
            // Update value with returned by handler
            return handlerReturn;
        }
    }
    return notHandled(value);
}


/**
 * Handle number types
 */
function numberHandler(value: unknown, typeInfo: TypeInfos, propertyKey: string): HandlerReturn {

    if (checkTypeExist(typeInfo, "number", propertyKey) && (isNumeric(value) || value === null)) {
        return {
            isHandled: true,
            value: value === null ? 0 : Number(value)
        };
    }

    return notHandled(value);
}

/**
 * Handle boolean types
 */
function booleanHandler(value: unknown, typeInfo: TypeInfos, propertyKey: string): HandlerReturn {

    if (checkTypeExist(typeInfo, "boolean", propertyKey) || (value && value === "false" || value === "true")) {
        return {
            isHandled: true,
            // note that we accept an empty string (for disabled attribute for example, an empty string means true)
            value: value != null && value !== "false" ? true : false
        };
    }

    return notHandled(value);
}

/**
 * Handle arrays types
 */
function arrayHandler(value: unknown, typeInfo: TypeInfos, propertyKey: string): HandlerReturn {
    if (checkTypeExist(typeInfo, ["Array", "string[]", "number[]"], propertyKey)) {
        let returnValue: Array<string | number>;

        // Handle empty array of undefined value
        if (value == null || (value as Array<unknown>).length === 0) {
            returnValue = [];
        }
        else {
            // Try to parse to array, convert numbers if needed
            try {
                returnValue = JSON.parse(value as string);
                returnValue = returnValue.map(value => {
                    return isNumeric(value) ? Number(value) : value;
                });
            } catch {
                if (typeof value === "object") {
                    returnValue = (value as Array<string | number>).map(value => {
                        return isNumeric(value) ? Number(value) : value;
                    });
                }

                return {
                    isHandled: false,
                    value: []
                };
            }
        }


        return {
            isHandled: true,
            value: returnValue
        };

    }

    return notHandled(value);
}

/**
 * Handle object types
 */
function objectHandler(value: unknown, typeInfo: TypeInfos, propertyKey: string): HandlerReturn {
    if (typeof value === "string") {
        try {
            JSON.parse(value);
        } catch (e) {
            return notHandled(value);
        }

        return {
            isHandled: true,
            value: value ? JSON.parse(value as string) : undefined
        };
    }
    return notHandled(value);
}
